// src/main/RentCollectionReport.js (Updated with correct data structure)

import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Button, Paper, Grid, Box, Chip, Divider, useTheme,
    Menu, MenuItem, IconButton, Tooltip, Card, CardContent, Avatar,
    TextField, FormControl, InputLabel, Select, Alert, Snackbar, CircularProgress,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    TablePagination, ToggleButton, ToggleButtonGroup, TableSortLabel, TableFooter,
    LinearProgress, Stack
} from '@mui/material';
import {
    TrendingUp, AttachMoney, Paid, MoneyOff, CurrencyExchange,
    PictureAsPdf, TableChart, GetApp, Print, Refresh, Search, Clear,
    CheckCircle, Cancel, Pending, ReceiptLong, BarChart, PieChart, ShowChart,
    ViewList
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title,
    Tooltip as ChartTooltip, Legend, ArcElement, PointElement, LineElement, Filler
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import useCustomToast from '../../hooks/useToast';
import {
    APIRentCollectionReport,
    APIRentCollectionExportExcel,
    APIRentCollectionExportPDF,
    APIRentCollectionPrint,
    APIRentCollectionStats,
    APIPropertyNames
} from '../../apiServices/APIs';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorrState';
import LoadingState from '../../components/LoadingState';
import { usePermission } from '../../hooks/usePermission';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, ArcElement, PointElement, LineElement, Filler);

const RentCollectionReport = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statistics, setStatistics] = useState(null);
    const [properties, setProperties] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [orderBy, setOrderBy] = useState('tenant_name');
    const [order, setOrder] = useState('asc');
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedProperty, setSelectedProperty] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [viewMode, setViewMode] = useState('table');
    const [chartType, setChartType] = useState('bar');

    const monthOptions = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let i = currentYear - 3; i <= currentYear + 2; i++) {
        yearOptions.push({ value: i, label: i.toString() });
    }

    const statusOptions = [
        { id: 'paid', name: 'Paid', color: 'success', icon: <CheckCircle /> },
        { id: 'partial', name: 'Partial', color: 'warning', icon: <Pending /> },
        { id: 'unpaid', name: 'Unpaid', color: 'error', icon: <Cancel /> }
    ];

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchProperties();
        fetchReport();
        fetchStatistics();
    }, [selectedMonth, selectedYear, selectedProperty, selectedStatus]);

    const fetchProperties = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listNames(APIPropertyNames);
            if (result.success) {
                console.log("result.data"+JSON.stringify(result.data))
                setProperties(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching properties:", error);
        }
    };

    const fetchReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const userBranchId = getUserDetails("branchId");
            const params = {
                month: selectedMonth,
                year: selectedYear,
                property_id: selectedProperty || '',
                status: selectedStatus || ''
            };
            
            const result = await apiService.listWithParams(userBranchId, APIRentCollectionReport, params);
            
            if (result.success) {
                const data = Array.isArray(result.data) ? result.data : [];
                setReportData(data);
                applyFilters(data, searchTerm);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load report data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const params = {
                month: selectedMonth,
                year: selectedYear,
                property_id: selectedProperty || ''
            };
            const result = await apiService.listStats(userBranchId, APIRentCollectionStats, params);
            if (result.success && result.data) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    const applyFilters = (data, search) => {
        let filtered = [...data];
        
        if (search) {
            const term = search.toLowerCase();
            filtered = filtered.filter(item => 
                (item.tenant_name || '').toLowerCase().includes(term) ||
                (item.apartment || '').toLowerCase().includes(term) ||
                (item.property_name || '').toLowerCase().includes(term) ||
                (item.payment_method || '').toLowerCase().includes(term)
            );
        }
        
        setFilteredData(filtered);
    };

    const handleSearch = (event) => {
        const term = event.target.value;
        setSearchTerm(term);
        applyFilters(reportData, term);
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);
        
        const sorted = [...filteredData].sort((a, b) => {
            const aVal = a[property] || '';
            const bVal = b[property] || '';
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return newOrder === 'asc' ? aVal - bVal : bVal - aVal;
            }
            if (aVal < bVal) return newOrder === 'asc' ? -1 : 1;
            if (aVal > bVal) return newOrder === 'asc' ? 1 : -1;
            return 0;
        });
        setFilteredData(sorted);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const clearFilters = () => {
        setSelectedMonth(new Date().getMonth() + 1);
        setSelectedYear(new Date().getFullYear());
        setSelectedProperty('');
        setSelectedStatus('');
        setSearchTerm('');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const getStatusBadge = (status) => {
        if (status === 'paid') return <Chip icon={<CheckCircle />} label="Paid" color="success" size="small" />;
        if (status === 'partial') return <Chip icon={<Pending />} label="Partial" color="warning" size="small" />;
        return <Chip icon={<Cancel />} label="Unpaid" color="error" size="small" />;
    };

    // Export handlers
    const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
    const handleExportClose = () => setExportAnchorEl(null);
    
    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export", "error");
            handleExportClose();
            return;
        }
        try {
            handleExportClose();
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('month', selectedMonth);
            formDataToSend.append('year', selectedYear);
            formDataToSend.append('property_id', selectedProperty);
            formDataToSend.append('status', selectedStatus);
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToExcel(APIRentCollectionExportExcel, formDataToSend);
            if (result?.success) showToast("Success", "Excel exported successfully", "success");
        } catch (error) {
            showToast("Error", "Failed to export Excel", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export", "error");
            handleExportClose();
            return;
        }
        try {
            handleExportClose();
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('month', selectedMonth);
            formDataToSend.append('year', selectedYear);
            formDataToSend.append('property_id', selectedProperty);
            formDataToSend.append('status', selectedStatus);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToPDF(APIRentCollectionExportPDF, formDataToSend);
            if (result?.success) showToast("Success", "PDF exported successfully", "success");
        } catch (error) {
            showToast("Error", "Failed to export PDF", "error");
        }
    };

    const handlePrintReport = async () => {
        if (!hasPermission('PRINT')) {
            showToast("Permission Denied", "You don't have permission to print", "error");
            return;
        }
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('month', selectedMonth);
            formDataToSend.append('year', selectedYear);
            formDataToSend.append('property_id', selectedProperty);
            formDataToSend.append('status', selectedStatus);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.print(APIRentCollectionPrint, formDataToSend);
            if (result?.success) showToast("Success", "Report printed successfully", "success");
        } catch (error) {
            showToast("Error", "Failed to print report", "error");
        }
    };

    // Chart data
    const chartData = {
        labels: filteredData.slice(0, 20).map(item => item.tenant_name?.substring(0, 15) || 'N/A'),
        datasets: [
            {
                label: 'Due Amount',
                data: filteredData.slice(0, 20).map(item => item.due_amount || 0),
                backgroundColor: 'rgba(54, 162, 235, 0.5)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1
            },
            {
                label: 'Paid Amount',
                data: filteredData.slice(0, 20).map(item => item.paid_amount || 0),
                backgroundColor: 'rgba(75, 192, 192, 0.5)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1
            },
            {
                label: 'Balance',
                data: filteredData.slice(0, 20).map(item => item.balance || 0),
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 1
            }
        ]
    };

    const pieChartData = {
        labels: ['Paid', 'Partial', 'Unpaid'],
        datasets: [{
            data: [
                statistics?.total_paid_count || 0,
                statistics?.total_partial || 0,
                statistics?.total_unpaid || 0
            ],
            backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(255, 206, 86, 0.5)', 'rgba(255, 99, 132, 0.5)'],
            borderColor: ['rgba(75, 192, 192, 1)', 'rgba(255, 206, 86, 1)', 'rgba(255, 99, 132, 1)'],
            borderWidth: 1
        }]
    };

    const lineChartData = {
        labels: filteredData.slice(0, 20).map(item => item.tenant_name?.substring(0, 15) || 'N/A'),
        datasets: [{
            label: 'Collection Rate %',
            data: filteredData.slice(0, 20).map(item => item.due_amount > 0 ? ((item.paid_amount || 0) / item.due_amount) * 100 : 0),
            borderColor: 'rgba(75, 192, 192, 1)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            fill: true,
            tension: 0.4
        }]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: { position: 'top' },
            tooltip: { callbacks: { label: (context) => `${context.dataset.label}: ${formatCurrency(context.raw)}` } }
        },
        scales: { y: { beginAtZero: true, ticks: { callback: (value) => formatCurrency(value) } } }
    };

    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: { position: 'bottom' },
            tooltip: { callbacks: { label: (context) => `${context.label}: ${context.raw}` } }
        }
    };

    const lineChartOptions = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: { position: 'top' },
            tooltip: { callbacks: { label: (context) => `Collection Rate: ${context.raw.toFixed(1)}%` } }
        },
        scales: { y: { beginAtZero: true, max: 100, title: { display: true, text: 'Collection Rate (%)' } } }
    };

    if (permissionLoading) return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    if (loading) return <LoadingState message="Loading Report Data..." variant="medical" size="large" fullPage />;
    if (error) return <ErrorState error={error} onRetry={fetchReport} title="Unable to Load Report" variant="medical" fullPage retryText="Retry Loading" />;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header with Filters */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                        Rent Collection Report
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <ToggleButtonGroup value={viewMode} exclusive onChange={(e, val) => val && setViewMode(val)} size="small">
                            <ToggleButton value="table"><ViewList /> Table</ToggleButton>
                            <ToggleButton value="chart"><BarChart /> Charts</ToggleButton>
                        </ToggleButtonGroup>
                        {hasPermission('EXPORT') && (
                            <>
                                <Button variant="contained" startIcon={<GetApp />} onClick={handleExportClick}>Export</Button>
                                <Button variant="contained" color="secondary" startIcon={<Print />} onClick={handlePrintReport}>Print</Button>
                            </>
                        )}
                    </Box>
                </Box>
                
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Month</InputLabel>
                            <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} label="Month">
                                {monthOptions.map(month => <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Year</InputLabel>
                            <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} label="Year">
                                {yearOptions.map(year => <MenuItem key={year.value} value={year.value}>{year.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Property</InputLabel>
                            <Select value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)} label="Property">
                                <MenuItem value="">All Properties</MenuItem>
                                {properties.map(prop => <MenuItem key={prop.id} value={prop.id}>{prop.property_name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Payment Status</InputLabel>
                            <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Payment Status">
                                <MenuItem value="">All</MenuItem>
                                {statusOptions.map(opt => <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth size="small" placeholder="Search by tenant, apartment, property..." value={searchTerm} onChange={handleSearch} InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} /> }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button startIcon={<Refresh />} onClick={() => { fetchReport(); fetchStatistics(); }}>Refresh</Button>
                            <Button startIcon={<Clear />} onClick={clearFilters} sx={{ ml: 1 }}>Clear Filters</Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Export Menu */}
            <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
                <MenuItem onClick={handleExportExcel}><TableChart fontSize="small" sx={{ mr: 1 }} /> Export to Excel (.xlsx)</MenuItem>
                <MenuItem onClick={handleExportPDF}><PictureAsPdf fontSize="small" sx={{ mr: 1 }} /> Export to PDF (.pdf)</MenuItem>
            </Menu>

            {/* Statistics Cards */}
            {statistics && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card elevation={2} sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box><Typography color="textSecondary" gutterBottom>Total Due</Typography><Typography variant="h6">{formatCurrency(statistics.total_due)}</Typography></Box>
                                    <Avatar sx={{ bgcolor: theme.palette.error.light, width: 56, height: 56 }}><MoneyOff sx={{ fontSize: 32 }} /></Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card elevation={2} sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box><Typography color="textSecondary" gutterBottom>Total Paid</Typography><Typography variant="h6" sx={{ color: theme.palette.success.main }}>{formatCurrency(statistics.total_paid)}</Typography></Box>
                                    <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}><Paid sx={{ fontSize: 32 }} /></Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card elevation={2} sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box><Typography color="textSecondary" gutterBottom>Outstanding Balance</Typography><Typography variant="h6" sx={{ color: theme.palette.warning.main }}>{formatCurrency(statistics.total_balance)}</Typography></Box>
                                    <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 56, height: 56 }}><CurrencyExchange sx={{ fontSize: 32 }} /></Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card elevation={2} sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box><Typography color="textSecondary" gutterBottom>Collection Rate</Typography><Typography variant="h6">{statistics.collection_rate || 0}%</Typography></Box>
                                    <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}><TrendingUp sx={{ fontSize: 32 }} /></Avatar>
                                </Box>
                                <LinearProgress variant="determinate" value={statistics.collection_rate || 0} sx={{ mt: 1, height: 8, borderRadius: 4 }} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: theme.palette.primary.main, '& th': { color: 'white', fontWeight: 'bold' } }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>
                                            <TableSortLabel active={orderBy === 'tenant_name'} direction={orderBy === 'tenant_name' ? order : 'asc'} onClick={() => handleRequestSort('tenant_name')} sx={{ color: 'white !important', '&.Mui-active': { color: 'white !important' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}>
                                                Tenant
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Property</TableCell>
                                        {/* <TableCell>Apartment</TableCell> */}
                                        <TableCell align="right">
                                            <TableSortLabel active={orderBy === 'due_amount'} direction={orderBy === 'due_amount' ? order : 'asc'} onClick={() => handleRequestSort('due_amount')} sx={{ color: 'white !important', '&.Mui-active': { color: 'white !important' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}>
                                                Due Amount
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel active={orderBy === 'paid_amount'} direction={orderBy === 'paid_amount' ? order : 'asc'} onClick={() => handleRequestSort('paid_amount')} sx={{ color: 'white !important', '&.Mui-active': { color: 'white !important' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}>
                                                Paid Amount
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel active={orderBy === 'balance'} direction={orderBy === 'balance' ? order : 'asc'} onClick={() => handleRequestSort('balance')} sx={{ color: 'white !important', '&.Mui-active': { color: 'white !important' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}>
                                                Balance
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Payment Date</TableCell>
                                        <TableCell>Payment Method</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => (
                                        <TableRow key={record.id || index} hover>
                                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell>
                                                <Typography fontWeight="bold">{record.tenant_name}</Typography>
                                                <Typography variant="caption" color="textSecondary">{record.tenant_phone}</Typography>
                                            </TableCell>
                                            <TableCell>{record.property_name}</TableCell>
                                            {/* <TableCell>{record.apartment}ss</TableCell> */}
                                            <TableCell align="right">{formatCurrency(record.due_amount)}</TableCell>
                                            <TableCell align="right" sx={{ color: theme.palette.success.main }}>{formatCurrency(record.paid_amount)}</TableCell>
                                            <TableCell align="right" sx={{ color: record.balance > 0 ? theme.palette.error.main : theme.palette.success.main }}>{formatCurrency(record.balance)}</TableCell>
                                            <TableCell>{record.payment_date || '-'}</TableCell>
                                            <TableCell>{record.payment_method_text || (record.payment_method ? record.payment_method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-')}</TableCell>
                                            <TableCell>{getStatusBadge(record.status)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredData.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                                <ReceiptLong sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
                                                <Typography color="text.secondary">No data found for the selected filters</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                                <TableFooter>
                                    <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                        <TableCell colSpan={4}><strong>Totals ({filteredData.length} records)</strong></TableCell>
                                        <TableCell align="right"><strong>{formatCurrency(filteredData.reduce((sum, r) => sum + (r.due_amount || 0), 0))}</strong></TableCell>
                                        <TableCell align="right"><strong>{formatCurrency(filteredData.reduce((sum, r) => sum + (r.paid_amount || 0), 0))}</strong></TableCell>
                                        <TableCell align="right"><strong>{formatCurrency(filteredData.reduce((sum, r) => sum + (r.balance || 0), 0))}</strong></TableCell>
                                        <TableCell colSpan={3}></TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
                    </Paper>
                </motion.div>
            )}

            {/* Chart View */}
            {viewMode === 'chart' && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <ToggleButtonGroup value={chartType} exclusive onChange={(e, val) => val && setChartType(val)} size="small">
                                    <ToggleButton value="bar"><BarChart /> Bar</ToggleButton>
                                    <ToggleButton value="line"><ShowChart /> Line</ToggleButton>
                                    <ToggleButton value="pie"><PieChart /> Pie</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                {chartType === 'bar' && <Bar data={chartData} options={chartOptions} height={400} />}
                                {chartType === 'line' && <Line data={lineChartData} options={lineChartOptions} height={400} />}
                                {chartType === 'pie' && <Pie data={pieChartData} options={pieChartOptions} height={400} />}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                <Typography variant="h6" gutterBottom>Summary</Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Stack spacing={2}>
                                    <Box><Typography variant="body2" color="textSecondary">Total Tenants</Typography><Typography variant="h5">{statistics?.total_tenants || 0}</Typography></Box>
                                    <Box><Typography variant="body2" color="textSecondary">Fully Paid</Typography><Typography variant="h5" color="success.main">{statistics?.total_paid_count || 0}</Typography></Box>
                                    <Box><Typography variant="body2" color="textSecondary">Partial Payment</Typography><Typography variant="h5" color="warning.main">{statistics?.total_partial || 0}</Typography></Box>
                                    <Box><Typography variant="body2" color="textSecondary">Unpaid</Typography><Typography variant="h5" color="error.main">{statistics?.total_unpaid || 0}</Typography></Box>
                                    <Divider />
                                    <Box><Typography variant="body2" color="textSecondary">Collection Rate</Typography><Typography variant="h6" color="primary.main">{statistics?.collection_rate || 0}%</Typography></Box>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </motion.div>
            )}
        </Container>
    );
};

export default RentCollectionReport;